import type { SxProps, Theme } from '@mui/material/styles';
import type { TaskPriority } from '@/hooks/useTasks';

/**
 * Card styling for a task, emphasised by priority. Urgent and high tasks get a
 * coloured left border and a stronger background tint so they stand out in the
 * list immediately; urgent tasks additionally receive a coloured box-shadow for
 * maximum visibility. Low tasks get a subtle left border so all four levels are
 * visually distinct.
 *
 * Note: the `low` case uses the sx-callback form `(theme) => ({...})` so that
 * `theme.palette.divider` is resolved at render time. MUI's SX system resolves
 * dot-notation palette paths (e.g. `error.main`) for specific properties, but
 * bare single-word tokens like `divider` are NOT resolved for `borderLeftColor`
 * — the browser would receive the literal string "divider". Using the callback
 * form is the safest approach for non-dot-notation semantic tokens.
 */
export function getTaskCardStyles(priority: TaskPriority): SxProps<Theme> {
  const base = {
    mb: 1.5,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  } as const;

  switch (priority) {
    case 'urgent':
      return {
        ...base,
        borderLeft: '6px solid',
        borderLeftColor: 'error.main',
        backgroundColor: 'rgba(220, 38, 38, 0.10)',
        boxShadow: '0 0 0 1px rgba(220, 38, 38, 0.25)',
        '&:hover': {
          backgroundColor: 'rgba(220, 38, 38, 0.16)',
          boxShadow: '0 0 0 2px rgba(220, 38, 38, 0.40)',
        },
      };
    case 'high':
      return {
        ...base,
        borderLeft: '4px solid',
        borderLeftColor: 'warning.main',
        backgroundColor: 'rgba(194, 65, 12, 0.08)',
        '&:hover': { backgroundColor: 'rgba(194, 65, 12, 0.14)' },
      };
    case 'low':
      // Use sx-callback so theme.palette.divider is resolved at render time.
      // Bare 'divider' token is not resolved by MUI SX for borderLeftColor.
      return (theme: Theme) => ({
        ...base,
        borderLeft: '3px solid',
        borderLeftColor: theme.palette.divider,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      });
    default:
      return {
        ...base,
        '&:hover': { backgroundColor: 'action.hover' },
      };
  }
}
